import { collection, doc, getDoc, getDocs, query, runTransaction, serverTimestamp, where, writeBatch } from "firebase/firestore";
import { auth, db } from "../firebase";
import { AI_SURVEY_ID, AI_SURVEY_NAME, AI_SURVEY_VERSION } from "./aiDigitalSkillsConfig";
import { EVENT_NAME, SURVEY_ID } from "./config";

export function makeTokenCode() {
  const bytes = new Uint32Array(1);
  crypto.getRandomValues(bytes);
  return `UUN-${String(bytes[0] % 1000000).padStart(6, "0")}`;
}

export async function submitSurvey(answers, isTest = false) {
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const code = makeTokenCode();
    const responseRef = doc(collection(db, "surveyResponses"));
    const tokenRef = doc(db, "foodTokens", code);
    try {
      const batch = writeBatch(db);
      const common = { surveyId: SURVEY_ID, eventName: EVENT_NAME, submittedAt: serverTimestamp(), foodTokenCode: code, tokenClaimed:false, tokenClaimedAt:null, tokenClaimedBy:null, responseVersion:1, isTest };
      batch.set(responseRef, { ...answers, postcodePrefix: answers.postcodePrefix.trim().toUpperCase(), oneChange: answers.oneChange.trim(), connectionRating:Number(answers.connectionRating), ...common });
      batch.set(tokenRef, { surveyResponseId:responseRef.id, surveyId:SURVEY_ID, tokenCode:code, claimed:false, createdAt:serverTimestamp(), claimedAt:null, claimedBy:null, isTest });
      await batch.commit();
      return { responseId: responseRef.id, tokenCode: code };
    } catch (error) { if (error.code !== "permission-denied" && error.code !== "already-exists") throw error; }
  }
  throw new Error("TOKEN_GENERATION_FAILED");
}

export async function submitAiDigitalSkillsSurvey(answers, isTest = false) {
  const responseRef = doc(collection(db, "surveyResponses"));
  const contactAllowed = answers.contactPermission === "Yes";
  const contactDetails = {
    name: contactAllowed ? answers.contactName.trim() : "",
    email: contactAllowed ? answers.contactEmail.trim().toLowerCase() : "",
    mobile: contactAllowed ? answers.contactMobile.trim() : "",
    preferredMethod: contactAllowed ? answers.preferredContactMethod : "",
    consent: contactAllowed && answers.contactConsent === true,
  };
  const payload = {
    surveyId: AI_SURVEY_ID,
    surveyVersion: AI_SURVEY_VERSION,
    eventName: AI_SURVEY_NAME,
    submittedAt: serverTimestamp(),
    workshopInterest: answers.workshopInterest,
    topics: answers.topics,
    topicsOther: answers.topicsOther.trim(),
    priorityTopic: answers.priorityTopic,
    digitalConfidence: answers.digitalConfidence,
    aiExperience: answers.aiExperience,
    motivations: answers.motivations,
    motivationsOther: answers.motivationsOther.trim(),
    deliveryPreference: answers.deliveryPreference,
    preferredTimes: answers.preferredTimes,
    barriers: answers.barriers,
    barriersOther: answers.barriersOther.trim(),
    deviceAccess: answers.deviceAccess,
    area: answers.area,
    riversideResident: answers.riversideResident,
    ageGroup: answers.ageGroup,
    youthInterest: answers.youthInterest,
    contactPermission: answers.contactPermission,
    contactDetails,
    finalComments: answers.finalComments.trim(),
    referralSource: answers.referralSource,
    referralSourceOther: answers.referralSourceOther.trim(),
    isTest,
  };
  const batch = writeBatch(db);
  batch.set(responseRef, payload);
  await batch.commit();
  return {
    responseId: responseRef.id,
    contactRequested: contactAllowed,
  };
}

export async function lookupToken(rawCode) {
  const code = rawCode.trim().toUpperCase();
  const snap = await getDoc(doc(db, "foodTokens", code));
  return snap.exists() ? { id:snap.id, ...snap.data() } : null;
}

export async function claimToken(code) {
  return runTransaction(db, async (tx) => {
    const tokenRef = doc(db, "foodTokens", code);
    const tokenSnap = await tx.get(tokenRef);
    if (!tokenSnap.exists()) throw new Error("NOT_FOUND");
    if (tokenSnap.data().claimed) throw new Error("ALREADY_CLAIMED");
    const claimedBy = auth.currentUser.uid;
    const claimedAt = serverTimestamp();
    tx.update(tokenRef, { claimed:true, claimedAt, claimedBy });
    tx.update(doc(db, "surveyResponses", tokenSnap.data().surveyResponseId), { tokenClaimed:true, tokenClaimedAt:claimedAt, tokenClaimedBy:claimedBy });
    return { ...tokenSnap.data(), claimed:true, claimedBy };
  });
}

export async function loadResponses(surveyId = SURVEY_ID) {
  const snap = await getDocs(query(collection(db, "surveyResponses"), where("surveyId", "==", surveyId)));
  return snap.docs.map((item) => ({ id:item.id, ...item.data() }));
}
